/**
 * Cross-component credits synchronisation.
 *
 * Several screens read `scan_credits` / `is_pro` from the `profiles` table.
 * After a payment callback (or any action that mutates credits server-side)
 * we need every mounted consumer to re-read the row without a hard reload.
 *
 * `notifyCreditsChanged()` pokes all live `useScanCredits()` instances.
 * `markPaymentPending()` opens a short window during which consumers poll,
 * covering the gap between Paystack confirming and our webhook writing the row.
 */

type Listener = () => void

const listeners = new Set<Listener>()

const PENDING_KEY = 'td_payment_pending_at'
/** How long after a payment we keep polling for the row to flip. */
export const PAYMENT_PENDING_WINDOW_MS = 90_000
/** Poll cadence while a payment is settling. */
export const PAYMENT_POLL_INTERVAL_MS = 4_000

export function subscribeToCreditsChanged(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function notifyCreditsChanged(): void {
  listeners.forEach((listener) => {
    try {
      listener()
    } catch (err) {
      console.error('[creditsSync] listener failed:', err)
    }
  })
}

/** Called right after a payment is confirmed, so consumers poll briefly. */
export function markPaymentPending(): void {
  try {
    sessionStorage.setItem(PENDING_KEY, String(Date.now()))
  } catch {
    // Private mode / storage disabled — polling simply won't be armed.
  }
}

export function clearPaymentPending(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY)
  } catch {
    // no-op
  }
}

/** True while we're inside the post-payment settling window. */
export function isPaymentPending(): boolean {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY)
    if (!raw) return false
    const startedAt = Number(raw)
    if (!Number.isFinite(startedAt)) return false
    if (Date.now() - startedAt > PAYMENT_PENDING_WINDOW_MS) {
      clearPaymentPending()
      return false
    }
    return true
  } catch {
    return false
  }
}
